import { type AppAbility, defineResource } from '@tnn/core'
import { z } from 'zod'
import { addDays } from '../hr/attendance'
import { bankAccountNumber, banks } from '../party'
import { paymentMethods } from '../tms/receipt'

/**
 * A person's money bag (ถุงเงิน), person by person — a module of its own, shown in Thananan OS and
 * Accounting: the money is a person's, whichever app took it. Each person has three accounts (`fund`):
 *
 * - **collections** (เงินต้องนำส่ง): taken from customers — counter receipts, payments,
 *   deposits on jobs. The company's money: it is passed on (a transfer the receiver confirms) or
 *   paid into the company's bank account; it should be 0 at the end of the day. Spending it is
 *   the exception (an expense the reserve could not cover), reviewed by accounting.
 * - **reserve** (เงินสดย่อย): a fixed amount head-office accounting entrusts to a person (an imprest)
 *   for change and small expenses. Spent on expenses with a receipt photo; topped back up by
 *   accounting for what was spent; returned when no longer needed. It is only ever spent or
 *   returned, never handed on to someone else (a transfer is collections only).
 * - **bond** (เงินประกัน): the person's own money the company holds as security, not in their hands.
 *   Taken from their pay each run up to a target payroll sets (payroll.compensation), or paid in
 *   (cash.bond); used only to cover a cash shortage or damage, by someone other than them; given
 *   back with their final payslip. Pay data: seen by the person and whoever sees everyone's cash,
 *   never a branch's lead (its entries are seen in no branch).
 *
 * Besides people, a safe (cash.safe) holds cash: the company's collections waiting to be banked, put in by whoever
 * holds them and counted in by the safe's custodians. Nobody records cash for someone else: whoever presses the button
 * holds the money, and accounting moves it (cash.move) when it was recorded in the wrong hands.
 *
 * Every move is a ledger entry (`cash.entry`): append-only, never edited or deleted — a
 * cancellation adds the reversing entry. A holder's balance in a fund is the sum of its entries.
 * Bank transfers (โอน) never pass through anyone's hands and have no entries.
 */

export const cashFunds = ['collections', 'reserve', 'bond'] as const
export type CashFund = (typeof cashFunds)[number]
/** The cash in someone's hands: what a transfer could ever carry (the bond is the company's to hold) */
const handFunds = ['collections', 'reserve'] as const

/** What made a cash entry: the document it comes from */
/**
 * `advance`: a salary advance (hr.advance) handed over in cash from the payer's reserve; `payslip`: a pay run's bond
 * deduction or refund (payroll.payslip), posted when the run is approved; `bond`: a bond paid in or used (cash.bond);
 * `move`: accounting moving cash between accounts (cash.move)
 */
export const cashSources = [
  'receipt',
  'payment',
  'transfer',
  'deposit',
  'reserve',
  'expense',
  'advance',
  'refund',
  'payslip',
  'bond',
  'move',
] as const
export type CashSource = (typeof cashSources)[number]

/**
 * Who pressed the button, and whether that was for someone else: cash was once recorded for people without a phone,
 * which is no longer done; the documents from then still say so, and the lists filter on it
 */
const recordedFields = {
  recordedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 3, hidden: true } },
  onBehalf: { readOnly: true, type: 'boolean', filter: true, list: { width: 110, priority: 3, hidden: true } },
} as const

/** A counted difference, or the review of an expense paid from collections: `none`, `pending`, then `approved` / `rejected` */
export const reviewStatuses = ['none', 'pending', 'approved', 'rejected'] as const
const reviewTones = { none: 'neutral', pending: 'warning', approved: 'success', rejected: 'danger' } as const

/**
 * cash.entry: one move of cash in or out of one person's hands, in one fund. Written by the
 * server only, when a document is submitted, edited after submit, received or cancelled.
 */
export const cashEntry = defineResource({
  app: 'cash',
  name: 'entry',
  path: 'cash-entries',
  schema: z.object({}),
  fields: {
    at: { readOnly: true, type: 'datetime', filter: true, list: { width: 160, priority: 1 } },
    /** Whose hands: a person, or a safe (`safeId`) */
    userId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 1 } },
    safeId: { readOnly: true, type: 'ref', ref: { resource: 'cash.safe' }, filter: true, list: { priority: 1 } },
    fund: { readOnly: true, type: 'select', options: cashFunds, filter: true, list: { width: 120, priority: 1 } },
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 2 } },
    source: { readOnly: true, type: 'select', options: cashSources, filter: true, list: { width: 130, priority: 2 } },
    /** The document, by id and by number */
    sourceId: { readOnly: true, list: false },
    sourceNumber: { readOnly: true, search: true, list: { width: 150, priority: 1 } },
    /** What happened, in words: "รับเงินสด ใบเสร็จ BKK1-6910-0001", "ยกเลิก…: กลับรายการ" */
    summary: { readOnly: true, search: true, list: { priority: 2 } },
    /** In (+) or out (−) */
    amount: { readOnly: true, type: 'money', list: { width: 130, align: 'end', priority: 1 } },
    /** The holder's cash in this fund after this entry */
    balance: { readOnly: true, type: 'money', list: { width: 130, align: 'end', priority: 2 } },
    /** Who caused it (the person who submitted, received or cancelled the document) */
    byUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
  },
  title: 'sourceNumber',
  columns: ['at', 'userId', 'safeId', 'fund', 'source', 'sourceNumber', 'summary', 'amount', 'balance', 'branchId', 'byUserId'],
  defaultSort: '-at',
  actions: ['read', 'export'],
  ownerField: 'userId',
  branchFields: ['branchId'],
  // Someone's security deposit is pay: a head of their team or branch sees their collections and reserve only
  othersOnly: { fund: { $in: ['collections', 'reserve'] } },
  card: { title: 'summary', subtitle: 'sourceNumber', meta: ['at', 'amount'], status: 'fund' },
})

export const cashTransferStatuses = ['draft', 'sent', 'received', 'cancelled'] as const

/**
 * cash.transfer (ใบโอนหนี้): collections — what is owed the company — passed from their holder to
 * another person who will hand them in (no cash passes between people, the debt does: counter staff
 * transfer theirs to the one who deposits), or put into a safe (`toSafeId`, นำเงินเข้าเซฟ). Never reserve: that is only spent, or returned
 * to accounting (cash.reserve), 422 at `fund`; transfers of reserve from before stay as history.
 * The sender is always whoever writes it. Before sending to a person, the sender may count
 * everything they hold (`counted`, one total): the difference from collections + reserve is a
 * shortage or surplus of collections, with the reason. Submitting sends it (waiting, out of the
 * sender's collections); the receiver takes it over (`receive`) or cancels it. Into a
 * safe, one of its custodians counts it in (`receivedCount`): the safe takes what was counted, and
 * a difference from what was sent stays with the sender. Either difference waits for a lead
 * (`approveVariance`, never the sender or whoever counted it): only once approved is it written off.
 */
export const cashTransfer = defineResource({
  app: 'cash',
  name: 'transfer',
  path: 'cash-transfers',
  schema: z.object({
    /** A person, or a safe (`toSafeId`): one of the two */
    toUserId: z.string().nullish(),
    toSafeId: z.string().nullish(),
    fund: z.enum(handFunds).default('collections'),
    amount: z.number().positive(),
    /** Optional count before sending to a person: everything the sender holds, both funds (one total, no denominations) */
    counted: z.number().min(0).nullish(),
    /** Why the count differs from the system's balance (required when it does) */
    varianceReason: z.string().trim().max(300).nullish(),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 150, priority: 1 } },
    sentAt: { readOnly: true, type: 'datetime', filter: true, list: { width: 160, priority: 1 } },
    /** The sender: whoever wrote it */
    fromUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 1 } },
    toUserId: { type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 1 }, form: { span: 6, custom: true } },
    toSafeId: { type: 'ref', ref: { resource: 'cash.safe' }, filter: true, list: { priority: 1 }, form: { span: 6, custom: true } },
    fund: { filter: true, list: { width: 120, priority: 2 }, form: { span: 6 } },
    amount: { type: 'money', list: { width: 130, align: 'end', priority: 1 }, form: { span: 6 } },
    counted: { type: 'money', list: { align: 'end', priority: 3, hidden: true }, form: { span: 6, section: 'count' } },
    /** Into a safe: what its custodian counted in (server-managed) */
    receivedCount: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    /** Who confirmed receiving it: the receiver, or the safe's custodian who counted it */
    receivedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    /**
     * To a person: counted − (collections + reserve) when sent. Into a safe: receivedCount − amount. Below 0 short, above
     * 0 over (server-managed)
     */
    variance: { readOnly: true, type: 'money', list: { align: 'end', priority: 2 } },
    varianceReason: { list: false, form: { span: 6, section: 'count' } },
    varianceStatus: {
      readOnly: true,
      type: 'select',
      options: reviewStatuses,
      filter: true,
      tones: reviewTones,
      list: { width: 140, priority: 2 },
    },
    varianceReviewedBy: { readOnly: true, list: { priority: 3, hidden: true } },
    varianceReviewedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    varianceReviewNote: { readOnly: true, list: false },
    status: {
      readOnly: true,
      type: 'select',
      options: cashTransferStatuses,
      filter: true,
      tones: { draft: 'neutral', sent: 'warning', received: 'success', cancelled: 'danger' },
      list: { width: 130, priority: 1 },
    },
    receivedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    ...recordedFields,
    /** The sender's home branch when sent */
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 3, hidden: true } },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'other' } },
  },
  title: 'number',
  columns: [
    'number',
    'sentAt',
    'fromUserId',
    'toUserId',
    'toSafeId',
    'fund',
    'amount',
    'status',
    'variance',
    'varianceStatus',
    'onBehalf',
    'counted',
    'receivedAt',
    'recordedByUserId',
    'branchId',
  ],
  sections: ['main', 'count', 'other'],
  defaultSort: '-sentAt',
  // `receive`: confirm cash sent to oneself, or count it into a safe one looks after;
  // `approveVariance`: approve or reject a shortage / surplus counted by someone else
  actions: ['read', 'create', 'update', 'delete', 'export', 'print', 'receive', 'approveVariance'],
  ownerField: ['fromUserId', 'toUserId', 'recordedByUserId', 'receivedByUserId'],
  mineFields: ['fromUserId', 'toUserId'],
  branchFields: ['branchId'],
  submittable: { allowOnSubmit: ['notes'], cancelReason: 'required' },
  card: { title: 'toUserId', subtitle: 'number', meta: ['sentAt', 'amount'], status: 'status' },
})

export const cashDepositStatuses = ['draft', 'deposited', 'verified', 'cancelled'] as const

/**
 * cash.deposit (ใบนำฝาก): collections paid into the company's bank account, with the bank's slip:
 * one's own, or a safe's (`safeId`) by one of its custodians or accounting. Out of the holder's
 * collections when submitted; accounting marks it verified once it is on the bank statement
 * (`verify`), after which it cannot be cancelled.
 */
export const cashDeposit = defineResource({
  app: 'cash',
  name: 'deposit',
  path: 'cash-deposits',
  schema: z.object({
    /** Paid in from a safe; otherwise from one's own collections */
    safeId: z.string().nullish(),
    depositedOn: z.iso.date(),
    amount: z.number().positive(),
    /** The company account paid into, as written on the slip (e.g. "กสิกรไทย 123-4-56789-0") */
    bankAccount: z.string().trim().min(1).max(100),
    /** The bank's transaction reference */
    reference: z.string().trim().max(100).nullish(),
    /** Photo of the deposit slip (file id): required */
    slipId: z.string().min(1),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 150, priority: 1 } },
    depositedOn: { filter: true, list: { width: 130, priority: 1 }, form: { span: 4 } },
    /** Whose cash: the depositor's own, or none when it came out of a safe */
    userId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 1 } },
    safeId: { type: 'ref', ref: { resource: 'cash.safe' }, filter: true, list: { priority: 1 }, form: { span: 4, custom: true } },
    amount: { type: 'money', list: { width: 130, align: 'end', priority: 1 }, form: { span: 4 } },
    bankAccount: { suggest: true, list: { priority: 2 }, form: { span: 4 } },
    reference: { search: true, list: { priority: 3 }, form: { span: 6 } },
    slipId: { type: 'file', list: false, form: { span: 6 }, import: false, export: false },
    status: {
      readOnly: true,
      type: 'select',
      options: cashDepositStatuses,
      filter: true,
      tones: { draft: 'neutral', deposited: 'warning', verified: 'success', cancelled: 'danger' },
      list: { width: 140, priority: 1 },
    },
    verifiedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    verifiedBy: { readOnly: true, list: { priority: 3, hidden: true } },
    ...recordedFields,
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 3, hidden: true } },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'other' } },
  },
  title: 'number',
  columns: [
    'number',
    'depositedOn',
    'userId',
    'safeId',
    'amount',
    'bankAccount',
    'status',
    'reference',
    'verifiedAt',
    'onBehalf',
    'branchId',
  ],
  sections: ['main', 'other'],
  defaultSort: '-depositedOn',
  // `verify`: accounting found it on the bank statement
  actions: ['read', 'create', 'update', 'delete', 'export', 'print', 'verify'],
  // The person whose cash it is, and whoever recorded it (a safe's custodian)
  ownerField: ['userId', 'recordedByUserId'],
  mineFields: ['userId'],
  branchFields: ['branchId'],
  submittable: { allowOnSubmit: ['notes', 'reference'], cancelReason: 'required' },
  card: { title: 'bankAccount', subtitle: 'number', meta: ['depositedOn', 'amount'], status: 'status' },
})

/** `issue`: accounting entrusts (or tops up) reserve cash to a person; `return`: the person gives reserve back */
export const reserveKinds = ['issue', 'return'] as const
/** Of an issue: `setup` raises the person's reserve amount (วงเงิน), `topUp` replaces what was spent */
export const reservePurposes = ['setup', 'topUp'] as const
export const cashReserveStatuses = ['draft', 'sent', 'received', 'cancelled'] as const

/**
 * cash.reserve (ใบให้ / เติม / คืนเงินสดย่อย): reserve cash moving between head-office accounting
 * and a person. An issue (`issue`: accounting) is `setup` — the person's reserve amount
 * grows by it — or `topUp` — it replaces expenses paid, the amount stays; the person confirms
 * (`receive`), and only then is it in their reserve. A return (by the person) leaves their reserve at once and lowers their
 * reserve amount; accounting confirms receiving it. A person's reserve amount = setups − returns.
 */
export const cashReserve = defineResource({
  app: 'cash',
  name: 'reserve',
  path: 'cash-reserves',
  schema: z.object({
    kind: z.enum(reserveKinds).default('issue'),
    purpose: z.enum(reservePurposes).nullish(),
    /** Issue: who receives the reserve. Return: whose reserve (oneself) */
    userId: z.string().min(1),
    amount: z.number().positive(),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 150, priority: 1 } },
    sentAt: { readOnly: true, type: 'datetime', filter: true, list: { width: 160, priority: 1 } },
    // Drawn by the app: only someone who may issue chooses; everyone else returns
    kind: { filter: true, tones: { issue: 'info', return: 'neutral' }, list: { width: 120, priority: 1 }, form: { span: 4, custom: true } },
    purpose: { filter: true, list: { width: 130, priority: 2 }, form: { span: 4, custom: true } },
    userId: { type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 1 }, form: { span: 4, custom: true } },
    amount: { type: 'money', list: { width: 130, align: 'end', priority: 1 }, form: { span: 6 } },
    status: {
      readOnly: true,
      type: 'select',
      options: cashReserveStatuses,
      filter: true,
      tones: { draft: 'neutral', sent: 'warning', received: 'success', cancelled: 'danger' },
      list: { width: 130, priority: 1 },
    },
    receivedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    /** Accounting (issue) or the holder (return): whoever sent it */
    sentByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 2 } },
    ...recordedFields,
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 3, hidden: true } },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'other' } },
  },
  title: 'number',
  columns: ['number', 'sentAt', 'kind', 'purpose', 'userId', 'amount', 'status', 'sentByUserId', 'receivedAt', 'onBehalf', 'branchId'],
  sections: ['main', 'other'],
  defaultSort: '-sentAt',
  // A holder creates returns; `issue`: create issues (accounting); `receive`: confirm one
  actions: ['read', 'create', 'update', 'delete', 'export', 'print', 'issue', 'receive'],
  ownerField: ['userId', 'sentByUserId', 'recordedByUserId'],
  mineFields: ['userId'],
  branchFields: ['branchId'],
  submittable: { allowOnSubmit: ['notes'], cancelReason: 'required' },
  card: { title: 'userId', subtitle: 'number', meta: ['sentAt', 'amount'], status: 'status' },
})

/**
 * `payIn`: the person hands cash in towards their bond (it goes into the receiver's collections); `shortage`: their
 * bond pays collections they owe but no longer have; `damage`: their bond pays for damage they caused; `refund`: it is
 * paid back to them (on leaving: within 7 days, section 10), in cash from the payer's collections or by transfer
 */
export const bondKinds = ['payIn', 'shortage', 'damage', 'refund'] as const
export type BondKind = (typeof bondKinds)[number]

/**
 * cash.bond (ใบรับ / หัก / คืนเงินประกัน): a person's bond paid in, used or paid back, recorded by accounting — never by
 * the person whose bond it is. Submitting moves it at once (numbered `BD6910-0001`): a pay-in adds to their bond and to
 * the collections of whoever took the cash; a shortage takes from their bond what it clears from their collections;
 * damage takes it from their bond; a refund takes it from their bond, paid in cash out of the payer's collections or by
 * transfer (its reference or slip). The person hears it. Cancelling (with a reason) reverses it. Their payslips take the
 * rest (payroll.compensation's target), and give back what is still held when they leave.
 */
export const cashBond = defineResource({
  app: 'cash',
  name: 'bond',
  path: 'cash-bonds',
  schema: z.object({
    kind: z.enum(bondKinds).default('payIn'),
    /** Whose bond */
    userId: z.string().min(1),
    amount: z.number().positive(),
    /** What the shortage or damage was (required for those) */
    reason: z.string().trim().max(300).nullish(),
    /** A refund: paid in cash (out of the payer's collections) or by transfer */
    method: z.enum(paymentMethods).nullish(),
    /** A refund by transfer: its reference, or a photo of the slip (one of them) */
    reference: z.string().trim().max(100).nullish(),
    slipId: z.string().nullish(),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 150, priority: 1 } },
    kind: {
      filter: true,
      tones: { payIn: 'success', shortage: 'warning', damage: 'danger', refund: 'info' },
      list: { width: 150, priority: 1 },
      form: { span: 6 },
    },
    userId: { type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 1 }, form: { span: 6, custom: true } },
    amount: { type: 'money', list: { width: 130, align: 'end', priority: 1 }, form: { span: 6 } },
    reason: { search: true, list: { priority: 2 }, form: { span: 12, custom: true } },
    method: { optionLabels: 'tms:receipt.options.method', list: { width: 110, priority: 3 }, form: { span: 4, custom: true } },
    reference: { list: false, form: { span: 4, custom: true } },
    slipId: { type: 'file', list: false, form: { span: 4, custom: true }, import: false, export: false },
    submittedAt: { readOnly: true, type: 'datetime', filter: true, list: { width: 160, priority: 2 } },
    /** Accounting: who took the cash, or used the bond */
    recordedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 2 } },
    /** The person's home branch when submitted */
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 3, hidden: true } },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'other' } },
  },
  title: 'number',
  columns: ['number', 'submittedAt', 'kind', 'userId', 'amount', 'reason', 'method', 'recordedByUserId', 'branchId'],
  sections: ['main', 'other'],
  defaultSort: '-submittedAt',
  actions: ['read', 'create', 'update', 'delete', 'export'],
  // The person sees their own; no lead sees a branch's (pay data: no `branchFields`)
  ownerField: ['userId', 'recordedByUserId'],
  mineFields: ['userId'],
  submittable: { allowOnSubmit: ['notes', 'reference'], cancelReason: 'required' },
  card: { title: 'userId', subtitle: 'number', meta: ['submittedAt', 'amount'], status: 'kind' },
})

/** Section 10: a security deposit goes back within 7 days of someone's last day */
export const BOND_REFUND_DAYS = 7

/** The day a leaver's security deposit must be back with them by (YYYY-MM-DD) */
export const bondRefundDueBy = (lastDay: string) => addDays(lastDay, BOND_REFUND_DAYS)

export const expenseCategories = ['fuel', 'toll', 'supplies', 'repair', 'meal', 'postage', 'other'] as const

/**
 * cash.expense (จ่ายเงินสดย่อย): an expense paid from one's reserve — fuel, tolls, supplies —
 * with a photo of the receipt (required). If the reserve does not cover it, the rest may come
 * from collections (`useCollections`, with the reason): that part waits for accounting
 * (`review`): approved, it is the company's expense; rejected, it goes back to being owed
 * (the payer's collections are put back up). No approval when the reserve covers it.
 */
export const cashExpense = defineResource({
  app: 'cash',
  name: 'expense',
  path: 'cash-expenses',
  schema: z.object({
    spentOn: z.iso.date(),
    category: z.enum(expenseCategories),
    amount: z.number().positive(),
    /** Who was paid: the shop, the station */
    payee: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(500),
    /** Photo of the receipt or bill (file id): required */
    receiptFileId: z.string().min(1),
    /** The truck it was for (fuel, repairs), if any */
    vehicleId: z.string().nullish(),
    /** The reserve does not cover it: take the rest from collections */
    useCollections: z.boolean().default(false),
    /** Why collections had to be used (required with `useCollections`) */
    collectionsReason: z.string().trim().max(300).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 150, priority: 1 } },
    spentOn: { filter: true, list: { width: 130, priority: 1 }, form: { span: 4 } },
    category: { filter: true, list: { width: 140, priority: 1 }, form: { span: 4 } },
    amount: { type: 'money', list: { width: 130, align: 'end', priority: 1 }, form: { span: 4 } },
    payee: { search: true, suggest: true, list: { priority: 2 }, form: { span: 6 } },
    vehicleId: { type: 'ref', ref: { resource: 'tms.vehicle' }, filter: true, list: { priority: 3, hidden: true }, form: { span: 6 } },
    description: { type: 'textarea', search: true, list: { priority: 2 }, form: { span: 12 } },
    receiptFileId: { type: 'file', list: false, form: { span: 12 }, import: false, export: false },
    useCollections: { list: false, form: { span: 6, section: 'collections', custom: true } },
    collectionsReason: { list: false, form: { span: 6, section: 'collections', custom: true } },
    /** How it was paid: from the reserve, the rest from collections (server-managed) */
    fromReserve: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    fromCollections: { readOnly: true, type: 'money', list: { align: 'end', priority: 2 } },
    reviewStatus: {
      readOnly: true,
      type: 'select',
      options: reviewStatuses,
      filter: true,
      tones: reviewTones,
      list: { width: 140, priority: 2 },
    },
    reviewedBy: { readOnly: true, list: { priority: 3, hidden: true } },
    reviewedAt: { readOnly: true, type: 'datetime', list: { priority: 3, hidden: true } },
    reviewNote: { readOnly: true, list: false },
    /** The payer: oneself, or (recorded for them) whose cash paid it — drawn by the app */
    /** Whose cash paid it: whoever wrote it */
    userId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 1 } },
    ...recordedFields,
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 3, hidden: true } },
  },
  title: 'number',
  columns: [
    'number',
    'spentOn',
    'userId',
    'category',
    'payee',
    'amount',
    'fromCollections',
    'reviewStatus',
    'description',
    'fromReserve',
    'vehicleId',
    'onBehalf',
    'branchId',
  ],
  sections: ['main', 'collections'],
  defaultSort: '-spentOn',
  // `review`: approve or reject the part paid from collections
  actions: ['read', 'create', 'update', 'delete', 'export', 'print', 'review'],
  ownerField: ['userId', 'recordedByUserId'],
  mineFields: ['userId'],
  branchFields: ['branchId'],
  submittable: { allowOnSubmit: ['description', 'vehicleId'], cancelReason: 'required' },
  card: { title: 'payee', subtitle: 'number', meta: ['spentOn', 'amount'], status: 'category' },
})

/** `requested`: submitted, waiting for approval; `approved`: waiting for accounting's transfer; `paid`: transferred back */
export const claimStatuses = ['draft', 'requested', 'approved', 'paid', 'rejected', 'cancelled'] as const
export type ClaimStatus = (typeof claimStatuses)[number]

/**
 * cash.claim (เบิกค่าใช้จ่าย): a company expense someone paid with their own money — fuel, a part, supplies — claimed
 * back with a photo of the receipt (required). Submitting asks for it (`requested`, `CL6910-0001` by `spentOn`); the
 * head of their branch or accounting approves it (`approved`, never their own) or rejects it with a note (`rejected`);
 * accounting transfers it back with the slip (`paid`) into their payroll bank account, copied onto the claim when it is
 * submitted, or the one the claim gives when they have none (`payBank…`, seen by them and whoever pays only). Cancelled
 * with a reason until paid. Nobody's cash moves (no ledger entries): the journal posts the expense as owed to them (2150)
 * and the transfer clears it.
 */
export const cashClaim = defineResource({
  app: 'cash',
  name: 'claim',
  path: 'cash-claims',
  schema: z.object({
    spentOn: z.iso.date(),
    category: z.enum(expenseCategories),
    amount: z.number().positive().max(1_000_000),
    /** Who was paid: the shop, the station */
    payee: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(500),
    /** Photo of the receipt or bill (file id): required */
    receiptFileId: z.string().min(1),
    /** The truck it was for (fuel, repairs), if any */
    vehicleId: z.string().nullish(),
    /** Where to transfer it back, given only by someone with no payroll bank account (copied from it otherwise) */
    payBank: z.enum(banks).nullish(),
    payBankAccount: bankAccountNumber,
    payBankAccountName: z.string().trim().max(120).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 150, priority: 1 } },
    spentOn: { filter: true, list: { width: 130, priority: 1 }, form: { span: 4 } },
    category: { filter: true, list: { width: 140, priority: 1 }, optionLabels: 'cash:expense.options.category', form: { span: 4 } },
    amount: { type: 'money', list: { width: 130, align: 'end', priority: 1 }, form: { span: 4 } },
    payee: { search: true, suggest: true, list: { priority: 2 }, form: { span: 6 } },
    vehicleId: { type: 'ref', ref: { resource: 'tms.vehicle' }, filter: true, list: { priority: 3, hidden: true }, form: { span: 6 } },
    description: { type: 'textarea', search: true, list: { priority: 2 }, form: { span: 12 } },
    receiptFileId: { type: 'file', list: false, form: { span: 12 }, import: false, export: false },
    payBank: { type: 'select', options: banks, optionLabels: 'banks', list: false, form: { span: 4, section: 'payTo' } },
    payBankAccount: { list: false, form: { span: 4, section: 'payTo' }, import: false, export: false },
    payBankAccountName: { list: false, form: { span: 4, section: 'payTo' }, import: false, export: false },
    status: {
      readOnly: true,
      type: 'select',
      options: claimStatuses,
      filter: true,
      tones: { draft: 'neutral', requested: 'warning', approved: 'info', paid: 'success', rejected: 'danger', cancelled: 'danger' },
      list: { width: 130, priority: 1 },
    },
    /** Who paid it and claims it back: whoever wrote it */
    userId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 1 } },
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 3, hidden: true } },
    approvedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    approvedAt: { readOnly: true, type: 'datetime', list: false },
    rejectNote: { readOnly: true, list: false },
    paidByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    paidAt: { readOnly: true, type: 'datetime', list: false },
    /** The day the transfer went out, its reference and slip (set by `pay`) */
    paidOn: { readOnly: true, type: 'date', filter: true, list: { width: 130, priority: 2 } },
    paidReference: { readOnly: true, list: false },
    slipId: { readOnly: true, type: 'file', list: false, import: false, export: false },
  },
  title: 'number',
  columns: ['number', 'spentOn', 'userId', 'category', 'payee', 'amount', 'status', 'paidOn', 'description', 'vehicleId', 'branchId'],
  sections: ['main', 'payTo'],
  defaultSort: '-spentOn',
  // `approve` (or reject): someone other than who claims it; `pay`: transfer it back once approved
  actions: ['read', 'create', 'update', 'delete', 'export', 'print', 'approve', 'pay'],
  ownerField: 'userId',
  mineFields: ['userId'],
  branchFields: ['branchId'],
  // Nothing changes once claimed: what is approved is what is paid (a wrong one is cancelled and claimed again)
  submittable: { allowOnSubmit: [], cancelReason: 'required' },
  card: { title: 'payee', subtitle: 'number', meta: ['spentOn', 'amount'], status: 'status' },
})

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

/**
 * Cash a counter receipt puts in someone's collections: its cash parts less the change or refund
 * given back in cash (a refund by transfer is the company's bank money, not their hands)
 */
export function receiptCashIn(receipt: {
  payments?: readonly { method: string; amount: number }[] | null
  refund?: number | null
  refundMethod?: string | null
}): number {
  const cash = (receipt.payments ?? []).filter((p) => p.method === 'cash').reduce((sum, p) => sum + (p.amount || 0), 0)
  const back = receipt.refundMethod === 'transfer' ? 0 : Number(receipt.refund) || 0
  return round2(cash - back)
}

/** Cash a customer payment (accounting.payment) puts in the collections of whoever records it: its cash parts (cheques are not cash) */
export function paymentCashIn(payment: { payments?: readonly { method: string; amount: number }[] | null }): number {
  return round2((payment.payments ?? []).filter((p) => p.method === 'cash').reduce((sum, p) => sum + (p.amount || 0), 0))
}

export const cashSafeStatuses = ['active', 'inactive'] as const

/**
 * cash.safe (เซฟ): a real safe the company keeps cash in, set up by accounting: where it is (a branch) and who looks
 * after it (`custodianUserIds`). Whoever holds collections puts them in (a cash.transfer to the safe) and one of its
 * custodians counts them in; a custodian or accounting pays them into the bank (a cash.deposit from the safe). A safe
 * holds collections only, its balance in the ledger like a person's (`cash.entry.safeId`). An inactive one takes
 * nothing more in.
 */
export const cashSafe = defineResource({
  app: 'cash',
  name: 'safe',
  path: 'cash-safes',
  schema: z.object({
    name: z.string().trim().min(1).max(100),
    branchId: z.string().nullish(),
    custodianUserIds: z.array(z.string().min(1)).min(1),
    status: z.enum(cashSafeStatuses).default('active'),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    name: { search: true, list: { priority: 1 }, form: { span: 6 } },
    branchId: {
      type: 'ref',
      ref: { resource: 'company.branch', where: { status: 'active' } },
      filter: true,
      list: { priority: 1 },
      form: { span: 6 },
    },
    /** Who counts cash in and pays it into the bank */
    custodianUserIds: {
      type: 'multiselect',
      ref: { resource: 'admin.user' },
      list: { priority: 2 },
      form: { span: 12, custom: true },
    },
    status: { filter: true, tones: { active: 'success', inactive: 'neutral' }, list: { width: 120, priority: 2 }, form: { span: 6 } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  title: 'name',
  columns: ['name', 'branchId', 'custodianUserIds', 'status'],
  defaultSort: 'name',
  actions: ['read', 'create', 'update', 'delete', 'export'],
  branchFields: ['branchId'],
  card: { title: 'name', subtitle: 'branchId', meta: ['custodianUserIds'], status: 'status' },
})

/**
 * Who puts cash into a safe (a debt transfer into one): accounting, who sets the safes up (and the super admin), from
 * the Accounting app.
 * Everyone else hands their collections on to a person or deposits them; a safe's custodians count in what accounting
 * puts there, and deposit from it
 */
export const putsIntoSafes = (ability: AppAbility) => ability.can('create', cashSafe.key)

/** What cash.move moves between: a person's collections or reserve, or a safe (collections only) */
export const moveFunds = handFunds

/**
 * cash.move (ใบโยกเงิน): accounting moves cash from one account to another — a person's collections or reserve, or a
 * safe — to put right money recorded in the wrong hands, or to move it between safes. Always with the reason. It takes
 * effect when submitted, numbered `MV6910-0001`, nobody confirming: no more than the source holds (422 at `amount`).
 * Cancelling (with a reason) puts it back.
 */
export const cashMove = defineResource({
  app: 'cash',
  name: 'move',
  path: 'cash-moves',
  schema: z.object({
    /** From a person (`fromUserId`, with `fromFund`) or a safe (`fromSafeId`): one of the two */
    fromUserId: z.string().nullish(),
    fromSafeId: z.string().nullish(),
    fromFund: z.enum(moveFunds).default('collections'),
    toUserId: z.string().nullish(),
    toSafeId: z.string().nullish(),
    toFund: z.enum(moveFunds).default('collections'),
    amount: z.number().positive(),
    reason: z.string().trim().min(1).max(300),
    notes: z.string().max(500).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 150, priority: 1 } },
    movedAt: { readOnly: true, type: 'datetime', filter: true, list: { width: 160, priority: 1 } },
    // Each side is drawn as one input (a person or a safe, and the person's fund): the safe and the fund ride along
    fromUserId: {
      type: 'ref',
      ref: { resource: 'admin.user' },
      filter: true,
      list: { priority: 1 },
      form: { span: 12, section: 'from', custom: true },
    },
    fromSafeId: {
      type: 'ref',
      ref: { resource: 'cash.safe' },
      filter: true,
      list: { priority: 1 },
      form: { section: 'from', hidden: true },
    },
    fromFund: { filter: true, list: { width: 120, priority: 3 }, form: { section: 'from', hidden: true } },
    toUserId: {
      type: 'ref',
      ref: { resource: 'admin.user' },
      filter: true,
      list: { priority: 1 },
      form: { span: 12, section: 'to', custom: true },
    },
    toSafeId: { type: 'ref', ref: { resource: 'cash.safe' }, filter: true, list: { priority: 1 }, form: { section: 'to', hidden: true } },
    toFund: { filter: true, list: { width: 120, priority: 3 }, form: { section: 'to', hidden: true } },
    amount: { type: 'money', list: { width: 130, align: 'end', priority: 1 }, form: { span: 6 } },
    reason: { search: true, list: { priority: 2 }, form: { span: 12 } },
    recordedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, filter: true, list: { priority: 3, hidden: true } },
    notes: { type: 'textarea', list: false, form: { span: 12 } },
  },
  sections: ['from', 'to', 'main'],
  title: 'number',
  columns: [
    'number',
    'movedAt',
    'fromUserId',
    'fromSafeId',
    'toUserId',
    'toSafeId',
    'amount',
    'reason',
    'fromFund',
    'toFund',
    'recordedByUserId',
  ],
  defaultSort: '-movedAt',
  actions: ['read', 'create', 'update', 'delete', 'export', 'print'],
  ownerField: ['fromUserId', 'toUserId', 'recordedByUserId'],
  mineFields: ['fromUserId', 'toUserId'],
  submittable: { allowOnSubmit: ['notes'], cancelReason: 'required' },
  card: { title: 'reason', subtitle: 'number', meta: ['movedAt', 'amount'] },
})

/** A count of everything held against collections + reserve: below 0 short, above 0 over (always the collections') */
export function cashVariance(counted: number, held: { collections: number; reserve: number }): number {
  return round2(counted - held.collections - held.reserve)
}

/** How an expense is paid: from the reserve first, the rest from collections when allowed (else `short` is left unpaid) */
export function expenseSplit(amount: number, reserve: number, useCollections: boolean) {
  const fromReserve = round2(Math.min(Math.max(reserve, 0), amount))
  const rest = round2(amount - fromReserve)
  return useCollections ? { fromReserve, fromCollections: rest, short: 0 } : { fromReserve, fromCollections: 0, short: rest }
}

/** What a top-up should bring the reserve back to: the reserve amount entrusted, less what is still held */
export function reserveTopUp(reserveAmount: number, reserveHeld: number): number {
  return round2(Math.max(reserveAmount - reserveHeld, 0))
}

/**
 * A run's bond deduction: the amount per run, no more than what is left to the target; nothing without both. A target
 * lowered below what is held takes nothing (the rest is given back when they leave, not before)
 */
export function bondDue(target: number | null | undefined, perRun: number | null | undefined, held: number): number {
  if (!target || !perRun || target <= 0 || perRun <= 0) return 0
  return round2(Math.min(perRun, Math.max(target - held, 0)))
}

/**
 * Settling everything today: the collections and reserve one hands back, less the bond the company gives back. Above 0
 * the person pays the company; below 0 the company pays the person
 */
export function clearingToday(held: { collections: number; reserve: number; bond: number }): number {
  return round2(held.collections + held.reserve - held.bond)
}
